import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useReducer, } from 'react';
import { AuthRequestError, clearSession, fetchGlobalConfig, getStoredSession, signIn as apiSignIn, signOutRemote, storeSession, } from './services/auth';
const authReducer = (state, action) => {
    switch (action.type) {
        case 'INIT':
            return {
                ...state,
                user: action.payload?.user ?? null,
                token: action.payload?.token ?? null,
                passwordExpiring: action.payload?.passwordExpiring ?? false,
                daysUntilExpiry: action.payload?.daysUntilExpiry,
                isLoading: false,
                status: action.payload ? 'authenticated' : 'signed-out',
            };
        case 'CONFIG_LOADED':
            return {
                ...state,
                isConfigLoading: false,
                captchaEnabled: action.payload.CF_CAPTCHA_ENABLE,
            };
        case 'CONFIG_FAILED':
            return {
                ...state,
                isConfigLoading: false,
                captchaEnabled: false,
            };
        case 'SIGN_IN_START':
            return { ...state, isLoading: true, error: null, status: 'signing-in' };
        case 'SIGN_IN_SUCCESS':
            return {
                ...state,
                isLoading: false,
                user: action.payload.user,
                token: action.payload.token,
                passwordExpiring: action.payload.passwordExpiring ?? false,
                daysUntilExpiry: action.payload.daysUntilExpiry,
                status: 'authenticated',
                error: null,
            };
        case 'SIGN_IN_ERROR':
            return { ...state, isLoading: false, error: action.payload, status: 'signed-out' };
        case 'SIGN_OUT':
            return {
                ...state,
                user: null,
                token: null,
                passwordExpiring: false,
                daysUntilExpiry: undefined,
                isLoading: false,
                error: null,
                status: 'signed-out',
            };
        default:
            return state;
    }
};
const AuthContext = createContext(null);
// ── Provider ───────────────────────────────────────────────────────────────────
export const AuthProvider = (props) => {
    const { children } = props;
    const [state, dispatch] = useReducer(authReducer, {
        user: null,
        token: null,
        isLoading: true,
        isConfigLoading: true,
        error: null,
        status: 'initializing',
        captchaEnabled: false,
        passwordExpiring: false,
        daysUntilExpiry: undefined,
    });
    useEffect(() => {
        const session = getStoredSession();
        dispatch({ type: 'INIT', payload: session });
    }, []);
    useEffect(() => {
        let isMounted = true;
        fetchGlobalConfig()
            .then((config) => {
            if (!isMounted)
                return;
            dispatch({ type: 'CONFIG_LOADED', payload: config });
        })
            .catch(() => {
            if (!isMounted)
                return;
            dispatch({ type: 'CONFIG_FAILED' });
        });
        return () => {
            isMounted = false;
        };
    }, []);
    const runSignIn = useCallback(async (credentials) => {
        const result = await apiSignIn(credentials);
        if (!result.session) {
            throw new AuthRequestError({ message: 'Invalid sign-in response from server.' });
        }
        storeSession(result.session);
        dispatch({ type: 'SIGN_IN_SUCCESS', payload: result.session });
    }, []);
    const signIn = useCallback(async (credentials) => {
        dispatch({ type: 'SIGN_IN_START' });
        try {
            await runSignIn(credentials);
        }
        catch (err) {
            const authErr = err instanceof AuthRequestError
                ? { message: err.message, code: err.code, status: err.status }
                : { message: err.message ?? 'Unable to sign in.' };
            dispatch({ type: 'SIGN_IN_ERROR', payload: authErr });
            // Re-throw so callers (e.g. SignInForm's onSignIn prop) can display the error
            throw err;
        }
    }, [runSignIn]);
    const signOut = useCallback(() => {
        const currentToken = state.token;
        clearSession();
        dispatch({ type: 'SIGN_OUT' });
        if (currentToken) {
            void signOutRemote(currentToken);
        }
    }, [state.token]);
    // Redirect to sign-in whenever any API fires the 'auth:unauthorized' event
    useEffect(() => {
        const handleUnauthorizedEvent = () => {
            clearSession();
            dispatch({ type: 'SIGN_OUT' });
            window.location.replace('/sign-in');
        };
        window.addEventListener('auth:unauthorized', handleUnauthorizedEvent);
        return () => {
            window.removeEventListener('auth:unauthorized', handleUnauthorizedEvent);
        };
    }, []);
    const getDefaultRoute = useCallback(() => {
        // No default route - let the application decide based on ponds count
        // This prevents automatic redirect to /landing before ponds check completes
        return null;
    }, []);
    return (_jsx(AuthContext.Provider, { value: {
            ...state,
            signIn,
            signOut,
            getDefaultRoute,
        }, children: children }));
};
// ── Hook ───────────────────────────────────────────────────────────────────────
export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx)
        throw new Error('useAuth must be used within <AuthProvider>');
    return ctx;
};
